import type { EntityDetail, EntitySummary, ReviewDTO } from "./api";
import { METHODOLOGY_FAQ, faqJsonLd } from "./faq";
import { personName, present } from "./people";

// The single source for head content (SPEC §11). Called by the SSR renderer to
// emit into <head>, and by the client on soft navigation. One implementation
// means the two can never disagree.

export interface Meta {
  title: string;
  description: string;
  canonical: string;
  ogType: string;
  jsonLd: object | null;
  robots?: string;
  /** Absolute URL. Without one, every share of this site is a bare text link. */
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  /** Rendered as BreadcrumbList JSON-LD and used by Google in the result line. */
  breadcrumbs?: { name: string; url: string }[];
  /** Sequential pages of one list, so a crawler can walk the whole set. */
  prev?: string;
  next?: string;
}

// The square site-wide mark, for pages that are not a single listing. Absolute
// because Open Graph rejects relative URLs.
const ogImage = (base: string) => `${base}/icon-512.png`;

// A listing gets its own 1200x630 card, drawn by /og from what the database
// actually holds. Wide, so the share renders as a full-bleed card rather than
// a thumbnail beside text.
const ogCard = (base: string, type: string, slug: string) => `${base}/og/${type}/${slug}.png`;

const SITE = "CryptoChit";
const clip = (s: string, n = 155) => (s.length <= n ? s : `${s.slice(0, n - 1).trimEnd()}…`);

export const canonicalFor = {
  home: (base: string) => `${base}/`,
  directory: (base: string, type: "token" | "company", page = 1) =>
    `${base}/${type === "token" ? "tokens" : "companies"}${page > 1 ? `?page=${page}` : ""}`,
  category: (base: string, slug: string, page = 1) =>
    `${base}/category/${slug}${page > 1 ? `?page=${page}` : ""}`,
  entity: (base: string, type: string, slug: string) => `${base}/${type}/${slug}`,
  user: (base: string, username: string) => `${base}/u/${username}`,
  methodology: (base: string) => `${base}/methodology`,
  privacy: (base: string) => `${base}/privacy`,
  about: (base: string) => `${base}/about`,
  // Sorted so the 24 orderings of a four-entity comparison collapse to one URL.
  compare: (base: string, ids: string[]) => `${base}/compare?ids=${[...ids].sort().join(",")}`,
};

export type MetaInput =
  | { route: "home"; base: string }
  | {
      route: "directory";
      base: string;
      type: "token" | "company";
      total: number;
      page?: number;
      pageCount?: number;
    }
  | {
      route: "category";
      base: string;
      slug: string;
      name: string;
      /** The category's own blurb, which beats a generated sentence. */
      description?: string | null;
      items: EntitySummary[];
      page?: number;
      pageCount?: number;
    }
  | { route: "entity"; base: string; entity: EntityDetail; reviews: ReviewDTO[] }
  | { route: "compare"; base: string; entities: EntitySummary[]; indexable: boolean }
  | { route: "user"; base: string; username: string; reviewCount: number }
  | { route: "methodology"; base: string }
  | { route: "privacy"; base: string }
  | { route: "about"; base: string }
  | { route: "notfound"; base: string };

export function metaFor(input: MetaInput): Meta {
  switch (input.route) {
    case "entity": {
      const e = input.entity;
      const label = e.symbol ? `${e.name} (${e.symbol})` : e.name;
      const scored = e.ratingWeighted != null && e.reviewCount > 0;

      // Tombstoned and pending reviews never appear in structured data.
      const reviews = input.reviews
        .filter((r) => r.status === "published")
        .slice(0, 10)
        .map((r) => ({
          "@type": "Review",
          // Spread rather than `?? undefined`: an absent property and a
          // property set to undefined serialise the same, but only one of them
          // is unambiguous to read back.
          ...(present(r.title) ? { name: present(r.title) } : {}),
          ...(present(r.body) ? { reviewBody: present(r.body) } : {}),
          datePublished: r.createdAt,
          author: {
            "@type": "Person",
            name: personName(r.author, "Reviewer"),
          },
          reviewRating: {
            "@type": "Rating",
            ratingValue: r.ratingOverall,
            bestRating: 5,
            worstRating: 1,
          },
        }));

      // Google requires a Product to carry at least one of offers, review or
      // aggregateRating. This site sells nothing and refuses to invent ratings,
      // so an unreviewed token has none of the three — and claiming Product
      // anyway made every one of them an invalid item in Search Console.
      //
      // The honest fix is to describe the page as what it currently is. A token
      // nobody has reviewed is a Thing: valid, no required properties, and no
      // rich result, which is right when there is nothing to put in one. It
      // becomes a Product the moment a real review gives it something to show.
      const type =
        e.type === "token" ? (scored || reviews.length > 0 ? "Product" : "Thing") : "Organization";

      return {
        title: `${label} reviews & rating · ${SITE}`,
        description: clip(
          e.shortDesc ??
            `${e.name} reviewed by verified holders and users on ${SITE}. ${e.reviewCount} reviews.`,
        ),
        canonical: canonicalFor.entity(input.base, e.type, e.slug),
        ogType: "website",
        image: ogCard(input.base, e.type, e.slug),
        imageWidth: 1200,
        imageHeight: 630,
        breadcrumbs: [
          {
            name: e.type === "token" ? "Tokens" : "Companies",
            url: canonicalFor.directory(input.base, e.type),
          },
          { name: e.name, url: canonicalFor.entity(input.base, e.type, e.slug) },
        ],
        jsonLd: {
          "@context": "https://schema.org",
          "@type": type,
          name: e.name,
          ...(present(e.shortDesc) ? { description: present(e.shortDesc) } : {}),
          ...(present(e.website) ? { url: present(e.website) } : {}),
          ...(scored
            ? {
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: e.ratingWeighted,
                  reviewCount: e.reviewCount,
                  bestRating: 5,
                  worstRating: 1,
                },
              }
            : {}),
          // An empty array is not "no reviews", it is the review property
          // present and saying nothing. Omit it entirely instead.
          ...(reviews.length > 0 ? { review: reviews } : {}),
        },
      };
    }
    case "category": {
      const page = input.page ?? 1;
      const pages = input.pageCount ?? 1;
      const suffix = page > 1 ? ` — page ${page} of ${pages}` : "";
      return {
        title: `${input.name} — ranked & reviewed${suffix} · ${SITE}`,
        description: clip(
          `${input.description ?? `Compare ${input.name.toLowerCase()} by verified reviews, ratings and objective on-chain signals.`}${suffix ? ` Page ${page} of ${pages}.` : ""}`,
        ),
        canonical: canonicalFor.category(input.base, input.slug, page),
        prev: page > 1 ? canonicalFor.category(input.base, input.slug, page - 1) : undefined,
        next: page < pages ? canonicalFor.category(input.base, input.slug, page + 1) : undefined,
        ogType: "website",
        image: ogImage(input.base),
        breadcrumbs: [{ name: input.name, url: canonicalFor.category(input.base, input.slug) }],
        jsonLd: {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: input.name,
          mainEntity: {
            "@type": "ItemList",
            itemListElement: input.items.slice(0, 25).map((item, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: canonicalFor.entity(input.base, item.type, item.slug),
              name: item.name,
            })),
          },
        },
      };
    }
    case "directory": {
      const label = input.type === "token" ? "Tokens" : "Companies";
      const page = input.page ?? 1;
      const pages = input.pageCount ?? 1;
      // Page 2 must not share page 1's title, or the set reads as duplicates.
      const suffix = page > 1 ? ` — page ${page} of ${pages}` : "";
      return {
        title: `${label} — reviews & ratings${suffix} · ${SITE}`,
        description: clip(
          `Browse ${input.total} crypto ${input.type === "token" ? "tokens" : "companies"} rated by verified reviewers on ${SITE}.${suffix ? ` Page ${page} of ${pages}.` : ""}`,
        ),
        // Self-referencing per page. Pointing every page at page 1 would tell
        // Google to drop pages 2 and beyond — which is precisely the listings
        // that had no link to them in the first place.
        canonical: canonicalFor.directory(input.base, input.type, page),
        prev: page > 1 ? canonicalFor.directory(input.base, input.type, page - 1) : undefined,
        next: page < pages ? canonicalFor.directory(input.base, input.type, page + 1) : undefined,
        ogType: "website",
        image: ogImage(input.base),
        breadcrumbs: [{ name: label, url: canonicalFor.directory(input.base, input.type) }],
        jsonLd: null,
      };
    }
    case "compare": {
      const names = input.entities.map((e) => e.name).join(" vs ");
      return {
        title: `${names || "Compare"} — side-by-side · ${SITE}`,
        description: clip(
          names
            ? `Compare ${names} on ratings, verified reviews and objective signals.`
            : `Compare crypto tokens and companies side by side on ${SITE}.`,
        ),
        canonical: canonicalFor.compare(input.base, input.entities.map((e) => e.id)),
        ogType: "website",
        robots: input.indexable ? undefined : "noindex",
        jsonLd: {
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: input.entities.map((e, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: canonicalFor.entity(input.base, e.type, e.slug),
            name: e.name,
          })),
        },
      };
    }
    case "user": {
      return {
        title: `@${input.username} — reviewer profile · ${SITE}`,
        description: clip(`${input.reviewCount} reviews published by @${input.username} on ${SITE}.`),
        canonical: canonicalFor.user(input.base, input.username),
        ogType: "profile",
        jsonLd: {
          "@context": "https://schema.org",
          "@type": "Person",
          name: input.username,
          url: canonicalFor.user(input.base, input.username),
        },
      };
    }
    case "methodology":
      return {
        title: `Methodology — how ${SITE} scores trust`,
        description:
          "How CryptoChit verifies reviewers, weights ratings by reputation, and computes the transparent Trust Score.",
        canonical: canonicalFor.methodology(input.base),
        ogType: "article",
        image: ogImage(input.base),
        breadcrumbs: [{ name: "Methodology", url: canonicalFor.methodology(input.base) }],
        // The page's own FAQ, serialised from the same array it renders.
        jsonLd: faqJsonLd(METHODOLOGY_FAQ),
      };
    case "privacy":
      return {
        title: `Privacy, cookies and analytics · ${SITE}`,
        description:
          "Exactly what CryptoChit stores, which cookies it sets, and which third parties your browser contacts — described from the code, not a template.",
        canonical: canonicalFor.privacy(input.base),
        ogType: "article",
        image: ogImage(input.base),
        breadcrumbs: [{ name: "Privacy", url: canonicalFor.privacy(input.base) }],
        jsonLd: null,
      };
    case "about":
      return {
        title: `About — who publishes ${SITE}, and how it is funded`,
        description:
          "What CryptoChit is, how it makes money, where its data comes from, and what is not for sale: ratings, rankings and the removal of reviews.",
        canonical: canonicalFor.about(input.base),
        ogType: "article",
        image: ogImage(input.base),
        breadcrumbs: [{ name: "About", url: canonicalFor.about(input.base) }],
        jsonLd: {
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: `About ${SITE}`,
          url: canonicalFor.about(input.base),
          // Points at the Organization declared on the home page rather than
          // describing a second, subtly different publisher.
          about: { "@id": `${input.base}/#organization` },
        },
      };
    case "notfound":
      return {
        title: `Not found · ${SITE}`,
        description: "That page doesn't exist.",
        canonical: `${input.base}/`,
        ogType: "website",
        jsonLd: null,
        robots: "noindex",
      };
    case "home":
    default:
      return {
        title: `${SITE} — know before you trust`,
        description:
          "Before you trust a crypto product with your money, see what real users experienced and whether they can prove they used it.",
        canonical: canonicalFor.home(input.base),
        ogType: "website",
        image: ogImage(input.base),
        jsonLd: {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebSite",
              "@id": `${input.base}/#website`,
              name: SITE,
              url: `${input.base}/`,
              publisher: { "@id": `${input.base}/#organization` },
              // Declared so a site: query can surface the internal search box.
              potentialAction: {
                "@type": "SearchAction",
                target: {
                  "@type": "EntryPoint",
                  urlTemplate: `${input.base}/tokens?q={search_term_string}`,
                },
                "query-input": "required name=search_term_string",
              },
            },
            {
              // Named publisher, because a review site with no identifiable
              // author behind it is the first thing a reader — or a model
              // weighing whether to cite it — has no way to check.
              "@type": "Organization",
              "@id": `${input.base}/#organization`,
              name: SITE,
              url: `${input.base}/`,
              logo: { "@type": "ImageObject", url: ogImage(input.base) },
              description:
                "A directory of crypto exchanges, wallets, protocols and tokens where reviews " +
                "can carry cryptographic proof that the reviewer used the product.",
            },
          ],
        },
      };
  }
}
